import {
  FloatingFocusManager,
  FloatingPortal,
  autoUpdate,
  flip,
  limitShift,
  offset,
  shift,
  useDismiss,
  useFloating,
  useInteractions,
} from '@floating-ui/react'
import { GridRenderEditCellParams, GridTreeNodeWithRender } from '@mui/x-data-grid-premium'
import { all as promiseAll } from 'bluebird'
import { Formik } from 'formik'
import { compact, isNil, isUndefined } from 'lodash'
import { Dispatch, SetStateAction, useCallback, useEffect, useState } from 'react'
import * as yup from 'yup'
import { Input } from '~/@/components/ui/input'
import { apolloClient } from '~/common/apollo-client'
import {
  BreadcrumbObjectType,
  DataType,
  GetBreadcrumbsDocument,
  useDeleteDocumentMutation,
  useUpdateTableCellMutation,
  useUpdateTableRowMutation,
} from '~/common/generated/apollo-operations'
import { useDeviceType } from '~/common/hooks/useDeviceQuery'
import { useTrackDocumentIds } from '~/common/hooks/useTrackDocumentIds'
import { useTypedTranslation } from '~/common/hooks/useTypedTranslation'
import { getTableCellInitialValues } from '~/common/utils/formik'
import { getTableColumnValidationSchema } from '~/common/utils/record'
import { getCellDataForUpdate } from '~/common/utils/table-cell'
import { useDossSnackbar } from '~/components/shared/DossSnackbar'
import {
  DetailedTableColumn,
  DetailedTableRow,
  DossTableCell,
  DossTableRow,
} from '~/components/tables/types'
import { CellValue } from '../records/add-record/AddRecordDialog'
import { Modal } from '../ui/molecules/modal/Modal'
import { EditTableCellForm } from './EditTableCellForm'

interface EditTableCellDialogProps {
  params: GridRenderEditCellParams<
    DossTableRow,
    DossTableCell,
    DetailedTableColumn,
    GridTreeNodeWithRender
  >
  cell?: DossTableCell | null
  row: DossTableRow
  value: string[] | string | boolean | number | null | undefined
  maxColWidth: number
  anchorEl: HTMLElement | null
  tableId: string
  setRowsData: Dispatch<SetStateAction<DetailedTableRow[]>>
}

export interface EditTableCellFormStates {
  isLoading: boolean
  setIsLoading: (isLoading: boolean) => void
  isSuccess: boolean
  setIsSuccess: (isSuccess: boolean) => void
  isFailure: boolean
  setIsFailure: (isFailure: boolean) => void
}

/**
 * Renders a dialog to edit cell inside a Doss table in edit mode
 */
export const EditTableCellDialog = ({
  params,
  cell,
  row,
  value,
  anchorEl,
  maxColWidth,
  tableId,
  setRowsData,
}: EditTableCellDialogProps) => {
  const { t } = useTypedTranslation()
  const snackbar = useDossSnackbar()
  const { isMobile } = useDeviceType()
  const [updateTableRow] = useUpdateTableRowMutation()

  const [updateTableCell] = useUpdateTableCellMutation({
    refetchQueries: [
      {
        query: GetBreadcrumbsDocument,
        variables: {
          input: {
            objectId: row.id,
            objectType: BreadcrumbObjectType.TABLE_ROW,
          },
        },
      },
    ],
  })

  const [deleteDocument] = useDeleteDocumentMutation({
    update(cache, { data }) {
      if (!data) {
        return
      }

      const normalizedId = cache.identify(data.deleteDocument)
      cache.evict({ id: normalizedId })
      cache.gc()
    },
  })

  const [isLoading, setIsLoading] = useState(false)
  const [isSuccess, setIsSuccess] = useState(false)
  const [isFailure, setIsFailure] = useState(false)
  const [isOpen, setIsOpen] = useState(true)

  const { documentIds, addDocumentId, removeDocumentId } = useTrackDocumentIds()

  // Initializing initialColWidth with initial value of maxColumnWidth
  // so when maxColWidth changes, initialColWidth will not change and the popover width remains the same
  const [initialColWidth] = useState(() => maxColWidth)

  // Floating UI setup
  const { refs, floatingStyles, isPositioned, context } = useFloating({
    open: isOpen,
    onOpenChange: setIsOpen,
    strategy: 'fixed',
    placement: 'bottom',
    whileElementsMounted: autoUpdate,
    middleware: [offset(-30), flip(), shift({ limiter: limitShift(), crossAxis: true })],
    elements: { reference: anchorEl },
  })

  const dismiss = useDismiss(context)
  const { getFloatingProps } = useInteractions([dismiss])

  const handleStopEditing = useCallback(() => {
    params.api.stopCellEditMode({ id: params.id, field: params.field })
  }, [params.api, params.field, params.id])

  // When dismissed via outside click or escape, stop cell editing
  useEffect(() => {
    if (!isOpen) {
      handleStopEditing()
    }
  }, [handleStopEditing, isOpen])

  const handleCancel = async () => {
    // If user uploaded any files, delete them
    await promiseAll(documentIds.map((documentId) => deleteDocument({ variables: { documentId } })))
    setIsOpen(false)
    handleStopEditing()
  }

  // Normalize undefined values for array types because for array type columns, undefined values
  // might occur due to Elasticsearch field-level security behavior, which can remove empty arrays
  // entirely from results when the user doesn't have admin permissions.
  const normalizedValue =
    cell && isUndefined(value) && cell.column.type === DataType.ARRAY ? [] : value
  const cellReady = !!cell && !isUndefined(normalizedValue)

  const validationSchema = cellReady
    ? yup.object().shape({
        [cell.column.id]: getTableColumnValidationSchema(cell.column, {
          allowOverwrittenLookup: false,
        }),
      })
    : yup.object()

  const initialValues = cellReady ? getTableCellInitialValues(cell, normalizedValue) : {}

  const handleSubmit = async (values: Record<string, CellValue>) => {
    if (!cell) {
      return
    }

    setIsLoading(true)

    const row: DossTableRow = params.row
    const dataForUpdate = compact(
      Object.keys(values).map((key) => {
        const keyCell = row.cells.find((cell) => cell.column.id === key)
        if (keyCell) {
          return getCellDataForUpdate(keyCell, values[key], snackbar.showError, t)
        }
        return null
      }),
    )

    if (dataForUpdate.length === 0) {
      setIsLoading(false)
      setIsSuccess(true)
      handleStopEditing()
      return
    }

    try {
      if (dataForUpdate.length > 1) {
        const { data } = await updateTableRow({
          variables: {
            input: {
              tableRowId: row.id,
              cells: dataForUpdate.map((cell) => ({
                tableColumnId: cell.tableColumnId,
                data: cell.cellData,
                lookupCellIds: cell.lookupCellIds,
                documentIds: cell.documentIds,
                userIds: cell.userIds,
              })),
            },
          },
        })

        if (!isNil(data)) {
          const updatedRow = data.updateTableRow
          setRowsData((prevRows) =>
            prevRows.map((prevRow) =>
              prevRow.id === row.id
                ? {
                    ...updatedRow,
                    name: prevRow.name,
                    createdAt: prevRow.createdAt,
                    createdBy: prevRow.createdBy,
                    hasChildRows: prevRow.hasChildRows,
                    rootWorkspace: prevRow.rootWorkspace,
                    currentButtonNodes: prevRow.currentButtonNodes,
                    processingWorkspaceButton: prevRow.processingWorkspaceButton,
                  }
                : prevRow,
            ),
          )
        }
      } else {
        const { data } = await updateTableCell({
          variables: {
            input: {
              id: cell.id,
              data: dataForUpdate[0].cellData,
              lookupCellIds: dataForUpdate[0].lookupCellIds,
              documentIds: dataForUpdate[0].documentIds,
              userIds: dataForUpdate[0].userIds,
            },
          },
        })

        if (!isNil(data)) {
          const updatedCell = data.updateTableCell
          setRowsData((prevRows) =>
            prevRows.map((prevRow) => {
              if (prevRow.id === row.id) {
                return {
                  ...prevRow,
                  cells: prevRow.cells.map((prevCell) =>
                    prevCell.id === cell.id ? updatedCell : prevCell,
                  ),
                }
              }
              return prevRow
            }),
          )
        }
      }

      snackbar.showSuccess(t('table_cells.update_success', { columnName: cell.column.name }))
      setIsSuccess(true)

      const deletedFileIds = compact(dataForUpdate.flatMap((data) => data.deletedFileIds))
      deletedFileIds.forEach((deletedFileId) => {
        const normalizedId = apolloClient.cache.identify({
          __typename: 'Document',
          id: deletedFileId,
        })
        apolloClient.cache.evict({ id: normalizedId })
        apolloClient.cache.gc()
      })
    } catch (error) {
      snackbar.showError(t('table_cells.update_failure', { errorMessage: error.message }))
      setIsFailure(true)
    } finally {
      setIsLoading(false)
      handleStopEditing()
    }
  }

  const formContent = cellReady ? (
    <Formik
      initialValues={initialValues}
      onSubmit={handleSubmit}
      validationSchema={validationSchema}
    >
      <EditTableCellForm
        addDocumentId={addDocumentId}
        cell={cell}
        failure={isFailure}
        handleCancel={handleCancel}
        initialColWidth={initialColWidth}
        loading={isLoading}
        maxColWidth={maxColWidth}
        params={params}
        removeDocumentId={removeDocumentId}
        row={row}
        setIsFailure={setIsFailure}
        setIsLoading={setIsLoading}
        setIsSuccess={setIsSuccess}
        success={isSuccess}
        tableId={tableId}
        value={normalizedValue}
      />
    </Formik>
  ) : null

  // For lookup or array of lookup type columns, display a full screen modal on mobile
  const isLookupOrArrayOfLookup =
    cellReady && (cell.column.type === DataType.LOOKUP || cell.column.innerType === DataType.ARRAY)

  if (isLookupOrArrayOfLookup && isMobile) {
    return (
      <Modal className="h-full" fullScreen open padContent={false} showCloseButton={false}>
        <div className="h-full overflow-hidden">{formContent}</div>
      </Modal>
    )
  }

  return (
    <FloatingPortal>
      {isOpen && (
        <FloatingFocusManager context={context} modal={false}>
          <div
            ref={refs.setFloating}
            {...getFloatingProps({
              onKeyDown: (e) => e.stopPropagation(),
            })}
            style={{
              ...floatingStyles,
              zIndex: 1300,
              opacity: isPositioned ? 1 : 0,
              pointerEvents: isPositioned ? undefined : 'none',
            }}
          >
            {cellReady ? (
              <div className="rounded-md border bg-white shadow-lg">{formContent}</div>
            ) : (
              <div className="h-full w-[180px] rounded-md border bg-white p-1 shadow-lg">
                <Input disabled placeholder={t('common.loading')} />
              </div>
            )}
          </div>
        </FloatingFocusManager>
      )}
    </FloatingPortal>
  )
}
